import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import { Tooltip } from '@/components/ui/tooltip';
import { AdminDataService } from '@/services/admin-data';
import { getStatusTooltip } from '@/utils/admin-helpers';
import type { AdminDashboardData } from '@/types/admin';

export function AppAdmin() {
  const navigate = useNavigate();
  
  // Get data from service
  const dashboardData: AdminDashboardData =
    AdminDataService.getInstance().getDashboardData();
  const { coacheeStats, usageStats, coachingThemes } = dashboardData;

  // Handle status card clicks
  const handleStatusClick = (status: string) => {
    navigate(`/app/admin/users?filter=${status.toLowerCase()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome back, Wei Ming!
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
              WM
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Wei Ming</p>
              <p className="text-xs text-gray-500">Octave Institute</p>
            </div>
          </div>
        </div>
      </header>

      <main className="px-8 py-8">
        {/* Coachee Status */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Coachee Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Thriving */}
            <Tooltip content={getStatusTooltip('Thriving')} position="bottom">
              <div 
                onClick={() => handleStatusClick('thriving')}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md hover:border-green-300 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Thriving
                      </p>
                      <p className="text-3xl font-bold text-gray-900">
                        {coacheeStats.thriving}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-500 transition-colors" />
                </div>
              </div>
            </Tooltip>

            {/* Managing */}
            <Tooltip content={getStatusTooltip('Managing')} position="bottom">
              <div 
                onClick={() => handleStatusClick('managing')}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md hover:border-yellow-300 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Managing
                      </p>
                      <p className="text-3xl font-bold text-gray-900">
                        {coacheeStats.managing}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-yellow-500 transition-colors" />
                </div>
              </div>
            </Tooltip>

            {/* Struggling */}
            <Tooltip content={getStatusTooltip('Struggling')} position="bottom">
              <div 
                onClick={() => handleStatusClick('struggling')}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md hover:border-red-300 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Struggling
                      </p>
                      <p className="text-3xl font-bold text-gray-900">
                        {coacheeStats.struggling}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors" />
                </div>
              </div>
            </Tooltip>
          </div>
        </motion.section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Usage Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Platform Usage
            </h3>
            <div className="space-y-6">
              <Tooltip content="Total number of user accounts created on the platform. Click to view all users." position="top">
                <div 
                  onClick={() => navigate('/app/admin/users')}
                  className="border-b border-gray-100 pb-4 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Accounts created
                      </p>
                      <p className="text-3xl font-bold text-gray-900">
                        {usageStats.accountsCreated}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </Tooltip>

              <Tooltip content="Users who have been active in the last 30 days. Click to view active users." position="top">
                <div 
                  onClick={() => navigate('/app/admin/users?filter=active')}
                  className="border-b border-gray-100 pb-4 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Active users
                      </p>
                      <div className="flex items-end space-x-3">
                        <p className="text-3xl font-bold text-gray-900">
                          {usageStats.activeUsers}
                        </p>
                        <div className="flex items-center text-primary text-sm bg-primary/10 px-2 py-1 rounded-lg">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          <span className="font-medium">
                            +{usageStats.activeUsersChange}%
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">vs. last month</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </Tooltip>

              <Tooltip content="Users who have completed more than 3 coaching sessions. Click to view repeat users." position="top">
                <div 
                  onClick={() => navigate('/app/admin/users?filter=repeat')}
                  className="border-b border-gray-100 pb-4 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Repeat users
                      </p>
                      <div className="flex items-end space-x-3">
                        <p className="text-3xl font-bold text-gray-900">
                          {usageStats.repeatUsers}
                        </p>
                        <div className="flex items-center text-primary text-sm bg-primary/10 px-2 py-1 rounded-lg">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          <span className="font-medium">
                            +{usageStats.repeatUsersChange}%
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">vs. last month</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </Tooltip>

              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Average session length
                </p>
                <div className="flex items-end space-x-3">
                  <div className="flex items-baseline space-x-1">
                    <p className="text-3xl font-bold text-gray-900">
                      {usageStats.averageSessionLength}
                    </p>
                    <span className="text-sm text-gray-500">min</span>
                  </div>
                  <div className="flex items-center text-primary text-sm bg-primary/10 px-2 py-1 rounded-lg">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    <span className="font-medium">
                      +{usageStats.sessionLengthChange}%
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">vs. last month</p>
              </div>
            </div>
          </motion.section>

          {/* Coaching Themes */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Top Coaching Themes
              </h3>
              <span className="text-sm text-gray-500">Most Popular</span>
            </div>
            <div className="space-y-1">
              {coachingThemes.map((theme, index) => {
                const maxSessions = Math.max(
                  ...coachingThemes.map((t) => t.sessions),
                );
                const percentage = (theme.sessions / maxSessions) * 100;

                return (
                  <div
                    key={theme.id}
                    className="group hover:bg-gray-50 transition-colors rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white bg-primary">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium text-gray-900 flex-1">
                          {theme.title}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-right">
                        <span className="text-sm font-bold text-gray-900">
                          {theme.sessions}
                        </span>
                        <span className="text-xs text-gray-500 w-12">
                          sessions
                        </span>
                      </div>
                    </div>
                    <div className="relative">
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-700 bg-primary"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
}
